import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { buildMechanismBody, deriveBeerProfile, MECHANISM_PARAGRAPHS } from "./quiz-beer-profile";
import { MM_0102 } from "./_experiments";

/**
 * BEER QUIZ -- cold-traffic destination for the beer-and-estrogen ad set.
 *
 * The angle: hops carry one of the strongest phytoestrogens in the food supply,
 * so the "beer belly" is framed as a hormone problem, not a calorie problem.
 * Persuasion spine (operator's approved text, MMLP 00__):
 * cut-everything-and-it-stayed absolution -> hops/phytoestrogen mechanism ->
 * "your beer is winning the hormone fight" -> the three-pathway close (make more
 * T, unlock what's handcuffed, stop the swap to estrogen).
 *
 * The verdict is the locked T-suppression surface with `estrogen-dominance`
 * forced Primary (see quizzes/quiz-beer-profile.ts) and the `beer` cohort
 * overlay themeing the Result / Projection / Offer copy.
 *
 * 10 single-choice screens + 3 interstitials (2 bridges + analyzing) = 13.
 * Design spec: docs/specs/2026-08-14-quiz-beer-design.md.
 *
 * Like quiz-v6 and cortisol, this quiz has no GLP-1 screener, so `glp1-user` is
 * not reachable from the classifier.
 */

const QUESTION_ORDER = [
  "age",
  "beer-intake",
  "gut-stubborn",
  "energy-3pm",
  "bounce-back",
  "info-absolution",
  "softness",
  "patience",
  "libido",
  "strength",
  "when-started",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    id: "beer-intake",
    type: "single",
    prompt: "No judgement… How many beers do you drink each week?",
    options: [
      { value: "most-nights", label: "A cold one most nights" },
      { value: "weekends", label: "Weekends are for bbq’s and beer" },
      { value: "few-here-there", label: "A few here and there" },
      { value: "barely", label: "Barely touch the stuff" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Beer isn’t just calories. Hops contain one of the strongest plant estrogens ever found. The hoppier the beer, the bigger the dose. This tells us how much of it you’re taking on every week.",
    },
  },
  {
    id: "gut-stubborn",
    type: "single",
    prompt: "Does your gut stick around no matter what you cut: carbs, calories, even the beer itself?",
    options: [
      { value: "wont-budge", label: "Yep. It will not budge" },
      { value: "shrinks-comes-back", label: "It shrinks a little, then comes right back" },
      { value: "starting-creep", label: "It’s starting to creep" },
      { value: "no-gut", label: "No gut here" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Belly fat isn’t just sitting there… It’s hijacking your hormones and telling your body to store more belly fat. That’s why cutting carbs or calories alone hardly helps.",
    },
  },
  {
    id: "energy-3pm",
    type: "single",
    // Boost-verbatim option VALUES (the deriver contract); labels are the beer
    // quiz's own, per the operator's text. Same convention as cortisol's copy of
    // this screen.
    prompt: "How’s your energy by 3pm?",
    options: [
      { value: "drained", label: "Dead. It’s caffeine, the couch, or “just resting my eyes”" },
      { value: "afternoon-slump", label: "Dragging… the afternoon slump hits hard" },
      { value: "slightly-tired", label: "A little slower, but I push through" },
      { value: "same-energy", label: "Same as when I woke up" },
    ],
  },
  {
    id: "bounce-back",
    type: "single",
    prompt:
      "How long does it take you to bounce back from a few beers now, compared to when you were in your 20s?",
    options: [
      { value: "next-day-derailed", label: "The next day is derailed" },
      { value: "next-morning", label: "I feel it the next morning but I’m fine by the afternoon" },
      { value: "slower-start", label: "Slower start, then I’m fine" },
      { value: "barely-notice", label: "I barely notice" },
    ],
  },
  {
    id: "info-absolution",
    type: "info",
    heading: "There’s a reason it’s called a BEER belly.",
    // Trimmed from six paragraphs (~900 chars) to four (~470) on operator
    // instruction 2026-08-14. Every beat of the original survives -- absolution,
    // hops mechanism, the T-down/estrogen-up consequence, the reframe -- the
    // word count does not. The two cut lines were the ones that only restated:
    // the "none of those things addressed the real issue" setup (folded into the
    // mechanism paragraph) and the "a few more questions" handoff, which the
    // always-visible sticky CTA now does better than a sentence could.
    body: [
      "You’ve cut carbs. You’ve counted calories. You’ve suffered through light beer. The gut stayed.",
      "None of it touched the real problem. Hops (the ingredient that gives beer its flavor) carry one of the most potent phytoestrogens in nature, and your body can’t tell it from the real thing.",
      "So your testosterone drops. Your estrogen climbs. Fat pools around your chest and stomach, the classic beer belly no amount of crunches will fix.",
      "This isn’t a fitness problem. It’s a hormone problem.",
    ],
    // Scan layer, same rule as Bridge 2: one phrase per paragraph. Read only
    // the orange and the whole case still lands -- gut stayed, hops are the
    // cause, estrogen climbs, it's hormonal.
    bodyAccents: [
      "The gut stayed.",
      "one of the most potent phytoestrogens in nature",
      "Your estrogen climbs.",
      "It’s a hormone problem.",
    ],
    // Pinned so Continue is visible from first paint at every scroll position,
    // rather than sitting below the article. With `stickyCta` the article
    // renders `body -> image` and the bar is a sibling above it; see the lifted
    // stickyCta/image check in _schema.ts.
    // Operator-supplied and confirmed final on 2026-08-14; the "guy at the grill with a
    // buffer reflection" note in the source doc is stale, not a pending asset.
    // Note this bridge carries the full mechanism, so unlike cortisol's
    // absolution screen it is NOT mechanism-silent -- deliberate: the beer/belly
    // link IS the ad's hook, so withholding it here would open a loop the ad
    // already closed.
    image: {
      src: "/static/images/quiz-beer-belly.webp",
      alt: "A beer being poured from the tap beside a man looking down at his soft belly",
    },
  },
  {
    id: "softness",
    type: "single",
    prompt:
      "Gotten softer in places a guy doesn’t want to get soft? Chest, gut, jawline, or below the belt?",
    options: [
      { value: "noticeably", label: "Noticeably" },
      { value: "starting-to", label: "Starting to" },
      { value: "maybe-little", label: "Maybe a little" },
      { value: "not-at-all", label: "Not at all" },
    ],
  },
  {
    id: "patience",
    type: "single",
    prompt: "Do you have less patience for the kids, the wife, the annoying co-worker?",
    options: [
      { value: "every-day", label: "Every day" },
      { value: "most-days", label: "Most days" },
      { value: "now-and-then", label: "Now and then" },
      { value: "cool-as-ever", label: "Cool as ever" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "How are things in the bedroom compared to a few years ago?",
    options: [
      { value: "barely-happening", label: "Barely happening" },
      { value: "body-wont-cooperate", label: "I want to but my body doesn’t always cooperate" },
      { value: "less-often", label: "Less often than it used to be" },
      { value: "no-problems", label: "Nope, no problems there" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Low testosterone and low drive go hand in hand. When your body’s trading T for estrogen every weekend, drive is usually the first thing to go… And your wife probably noticed before you did.",
    },
  },
  {
    id: "strength",
    type: "single",
    prompt:
      "Do workouts, or even just yard work, feel exhausting now? Less strength, slower recovery, less to show for it?",
    options: [
      { value: "big-drop", label: "Big drop" },
      { value: "noticeable", label: "Noticeable" },
      { value: "slight", label: "Slight" },
      { value: "still-strong", label: "Still strong as ever" },
    ],
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you start noticing these signs creeping in?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    id: "info-mechanism",
    type: "info",
    // Cut from 25 words to 6 on 2026-08-17. The original ran SEVEN lines of
    // uppercase Martian Mono -- ~380px of a 667px screen before a single word of
    // body -- which was the same "too long to read" problem that trimmed Bridge
    // 1. The dropped clause ("and your testosterone is probably paying for it")
    // is not lost: it is what the first three paragraphs below say.
    heading: "Your beer is winning the hormone fight.",
    // Authored in quizzes/quiz-beer-profile.ts so this static fallback and the
    // `deriveBody` personalization cannot drift apart.
    body: [...MECHANISM_PARAGRAPHS],
    // Rewrites paragraph 1 to name the signs he actually reported, rendered in
    // mars-green. Falls back to the static body above when he reported fewer
    // than two.
    deriveBody: buildMechanismBody,
    // A second reading path: read only the orange and the argument still lands.
    // One phrase per paragraph, no more -- the point is a scan layer, not
    // decoration.
    // Four accents, not five, and every one short enough to read as a fragment.
    // Two rules learned by rendering it wrong first:
    //   - Keep each accent to roughly a phrase. The first draft accented "It's
    //     the hormone behind your muscle, your energy, your drive, your edge",
    //     which is three full lines of orange -- a highlighter, not a scan layer.
    //   - Never accent the paragraph `deriveBody` personalizes. It already
    //     carries up to three green runs, and orange on top of that leaves the
    //     sentence with more coloured words than plain ones.
    bodyAccents: [
      "makes you you",
      "tossing lighter fluid",
      "You can barely use what you’ve got",
      "You don’t have to give up beer",
    ],
    // Sits between "here is what it looks like when your T drops" and "the beer
    // is accelerating it", which is exactly the claim it evidences. Pinned to
    // the end (the old default) it would have landed after the closing
    // hand-off line.
    stat: {
      value: "1% a year",
      label: "Your natural testosterone decline after 30",
      cite: "Feldman et al., Massachusetts Male Aging Study, 2002",
      afterParagraph: 1,
    },
    // The one genuinely parallel list on this screen, and the only place bullets
    // earn their keep here -- the rest of the bridge is narrative whose cadence
    // a list would flatten. These three map 1:1 to the PATHWAY cards the user
    // meets later on the protocol page.
    bullets: {
      items: [
        "Make more T.",
        "Unlock the T that’s handcuffed.",
        "Stop the swap to estrogen.",
      ],
      afterParagraph: 4,
    },
    // Pinned for the same reason as Bridge 1: this body is six paragraphs, so
    // an in-flow Continue would sit well below the fold.
    // The animated T-decline chart, paying off the 1%-a-year paragraph above.
    // Operator-supplied 7-frame source; re-encoded to animated WebP with the
    // authored frame timing preserved (6 x 200ms draw, then a 1400ms hold on the
    // finished chart). Do NOT re-encode at a flat frame rate -- it destroys the
    // hold and triples the file size.
    image: {
      src: "/static/images/quiz-beer-t-decline.webp",
      alt: "A chart showing testosterone declining steadily with age, from 100 percent in your twenties down through your seventies",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    heading: "Analyzing your results…",
    body: [
      "Counting the signs you just reported, and measuring how much testosterone your six pack could be costing you.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizBeerQuiz = defineQuiz({
  id: "quiz-beer",
  slug: "quiz-beer",
  experiment: MM_0102,
  meta: {
    title: "Is Beer Behind Your Belly",
    introHeadline: "Is beer the true reason your belly won’t budge?",
    introAccent: "true",
    introBody:
      "There are 8 signs your six pack could be hijacking your hormones. See how many you have in this 2-minute quiz.",
    testimonial: {
      // Operator-supplied entry proof: quiz-benefit register, angle-matched to
      // the gut, names no product and no number.
      // TODO(growth): back the "Verified Review" tag with a real review before
      // relying on it -- same open item as the cortisol quiz's entry proof.
      quote:
        "Two minutes. Finally explained why the gut wouldn’t budge no matter what I cut.",
      author: "DAVE, Verified Review",
      // Operator's call 2026-08-14: reuse the cortisol avatar rather than block
      // the launch on a new one. Copied to a per-quiz path (byte-identical to
      // cortisol-testimonial-avatar.webp) so swapping in a purpose-built face
      // later is a one-file change that cannot disturb the cortisol quiz.
      // NEVER leave this field unset -- the fallback is a 10.1 MB PNG (rule 19).
      avatar: "/static/images/quiz-beer-testimonial-avatar.webp",
    },
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const gut = answers["gut-stubborn"];
    const energy = answers["energy-3pm"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const strength = answers["strength"];

    // Note: beer-intake, bounce-back, softness, and patience are not read here --
    // they are persuasion / severity screens, not routing signals (they feed the
    // deriver via quizzes/quiz-beer-profile.ts). The classifier routes on age,
    // gut-stubborn, energy, onset, libido, and strength only.

    // Performance-focused: no gut, but the gym has stopped paying out. Runs
    // first -- a lean user never reaches trt-alternative even with a long-running
    // collapse. Intentional: body shape signals audience, same as cortisol.
    if (gut === "no-gut" && (strength === "big-drop" || strength === "noticeable" || energy === "drained")) {
      return "performance";
    }

    // TRT-alternative: long-running collapse across energy + drive.
    if (
      onset === "over-year" &&
      energy === "drained" &&
      (libido === "barely-happening" || age === "50-plus")
    ) {
      return "trt-alternative";
    }

    // Skeptical buyer: can't place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ with a gut that has stopped responding.
    if (
      (age === "50-plus" || age === "40-49") &&
      (gut === "wont-budge" || gut === "shrinks-comes-back")
    ) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveBeerProfile,
});
